import { keyOfObject } from 'base/helpers';
import classNames from 'classnames';
import React, { ComponentProps } from 'react';
import ReactFocusLock from 'react-focus-lock';
import { CSSTransition } from 'react-transition-group';
import { Button } from './button';
import { ColoredIcon, renderIcon } from './colored_icon';
import {
  IconArrowForward,
  IconCheck,
  IconChevronDown,
  IconChevronRight,
} from './icons';
import { FormInput } from './input';
import { Layer } from './Layer';
import { Inline, Rows } from './layout';
import { globalResizeObserver } from './measure';
import styles from './menu.css';
import {
  AnyMenuItem,
  MenuItemProps,
  MenuModel,
  SubMenuItemProps,
} from './menu_model';
import {
  PlacementFunction,
  Tooltip,
  usePinElementPlacement,
  usePinPointPlacement,
} from './tooltip';
import { TextSmallLight, TextTiny, TextTinyLight } from './typography';

const Menu = React.forwardRef<
  HTMLDivElement,
  {
    model: MenuModel;
    className?: string;
    isRoot: boolean;

    closeRootMenu: () => void;
    closeThisMenu: () => void;
    onMouseEnter?: (e: React.MouseEvent) => void;
    onMouseLeave?: () => void;
    onPointerDown?: (e: React.PointerEvent) => void;
  } & MenuDisplayOptions
>(
  (
    {
      model,
      className,
      closeRootMenu,
      closeThisMenu,
      isRoot,
      search: searchOptsRaw,
      menuVariant = 'regular',
      ...passthrough
    },
    propRef,
  ) => {
    const [search, setSearch] = React.useState('');
    const [highlightedIndex, setHighlightedIndex] = React.useState(-1);
    const [heightBeforeSearch, setHeightBeforeSearch] = React.useState(0);
    const ref = React.useRef<HTMLDivElement>(null);
    const submenus = React.useRef<MenuChildProps['submenus']>([]);

    const searchOpts =
      searchOptsRaw === 'hidden' ? HIDDEN_SEARCH_OPTIONS : searchOptsRaw;
    const hasSearchButton = searchOpts?.button != null;
    const items = React.useMemo((): AnyMenuItem[] => {
      const allItems = typeof model === 'function' ? model(search) : model;
      if (!search) return allItems;
      const filtered = collapseDividers(
        allItems.flatMap((i) => filterAndFlatten(i, search)),
      );
      return filtered.length === 0
        ? hasSearchButton
          ? []
          : [{ type: 'text', label: 'No items' }]
        : filtered;
    }, [model, search, hasSearchButton]);
    const interactiveItems = React.useMemo(() => {
      return items.filter((i) => i.type === 'item' || i.type === 'submenu');
    }, [items]);
    const highlighted = interactiveItems[highlightedIndex];

    const onKeyDown = React.useCallback(
      (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Escape') {
          e.stopPropagation();
          e.preventDefault();
          closeThisMenu();
        } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.stopPropagation();
          e.preventDefault();
          const dir = e.key === 'ArrowDown' ? 1 : -1;
          const nextIndex =
            (highlightedIndex + dir + interactiveItems.length) %
            interactiveItems.length;
          setHighlightedIndex(nextIndex);
        } else if (e.key === 'Enter' && highlighted) {
          e.stopPropagation();
          e.preventDefault();
          if (highlighted.type === 'item') {
            if (highlighted.disabled) {
              // add a toast or something?
            } else if (highlighted.onClick) {
              highlighted.onClick();
            } else if (highlighted.href) {
              const target = highlighted.target || '_self';
              if (target === '_self') {
                window.location.href = highlighted.href;
              } else {
                window.open(highlighted.href, target);
              }
            }
            closeRootMenu();
          } else if (highlighted.type === 'submenu') {
            if (highlighted.disabled) {
              // add a toast or something?
            } else {
              const sub = submenus.current.find((s) => s.item === highlighted);
              sub?.toggle();
              setTimeout(() => sub?.menuRef.current?.focus());
            }
          }
        } else if (e.key === 'Enter' && searchOpts?.button != null) {
          e.stopPropagation();
          e.preventDefault();
          searchOpts.button.onSearch(search);
          closeRootMenu();
        }
      },
      [
        highlighted,
        searchOpts,
        closeThisMenu,
        highlightedIndex,
        interactiveItems.length,
        closeRootMenu,
        search,
      ],
    );

    // Fix the height when we enter search mode, so that the menu doesn't jump
    React.useEffect(() => {
      const el = ref.current;
      if (search || !el) return;
      return globalResizeObserver.observe(el, () => {
        setHeightBeforeSearch(el.clientHeight);
      });
    }, [search]);

    // Scroll the highlighted item into view, in search mode (where the height is fixed)
    React.useEffect(() => {
      const el = ref.current;
      if (!search || !el) return;
      const highlightedEl = el.querySelector(`.${styles.isHighlighted}`);
      if (!highlightedEl) return;
      highlightedEl.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      });
    }, [highlightedIndex, search]);

    const onChangeSearch = React.useCallback((v: string) => {
      setSearch(v);
      setHighlightedIndex(0);
    }, []);

    return (
      <div
        ref={useComposedRefs(propRef, ref)}
        style={{
          maxHeight: search
            ? heightBeforeSearch + (searchOpts?.hideWhenEmpty ? 48 : 0)
            : '',
        }}
        className={classNames(styles.menu, styles[menuVariant], className)}
        onKeyDown={onKeyDown}
        tabIndex={0}
        {...passthrough}
      >
        {isRoot && (
          <MenuSearch
            value={search}
            onChange={onChangeSearch}
            closeRootMenu={closeRootMenu}
            menuVariant={menuVariant}
            {...searchOpts}
          />
        )}
        <div
          className={classNames(
            styles.menuContent,
            items.length === 0 && styles.isEmpty,
          )}
        >
          {items.map((item) => {
            const key = keyOfObject(item);
            const childProps = {
              closeRootMenu,
              submenus: submenus.current,
              isHighlighted: highlighted === item,
              onMouseEnter: () =>
                setHighlightedIndex(interactiveItems.indexOf(item)),
            };

            if (item.type === 'divider') {
              return <MenuDivider key={key} />;
            } else if (item.type === 'text') {
              return (
                <MenuText key={key} size={item.size}>
                  {item.label}
                </MenuText>
              );
            } else if (item.type === 'submenu') {
              return <SubMenuItem key={key} item={item} {...childProps} />;
            } else {
              return (
                <MenuItemView
                  key={key}
                  {...item}
                  {...childProps}
                  onMouseEnter={(e) => {
                    childProps.onMouseEnter();
                    item.onMouseEnter?.(e);
                  }}
                />
              );
            }
          })}
        </div>
      </div>
    );
  },
);

export const MenuInternalForPlayground = Menu;

/** Applies a search to the menu */
function filterAndFlatten(item: AnyMenuItem, search: string): AnyMenuItem[] {
  if (item.type === 'text') {
    return matchesSearch(item, search) ? [item] : [];
  } else if (item.type === 'divider') {
    return [item];
  } else if (item.type === 'submenu') {
    const outerMatches = matchesSearch(item, search);
    const innerItems =
      typeof item.menu === 'function' ? item.menu(search) : item.menu;
    let innerMatches = collapseDividers(
      innerItems
        .map((i) => filterAndFlatten(i, outerMatches ? '*' : search))
        .flat(),
    );
    if (innerMatches.length === 0) return [];
    if (item.disabled) {
      innerMatches = innerMatches.map((i) => ({
        ...i,
        disabled: true,
        disabledReason: item.disabledReason,
      }));
    }
    return [
      { type: 'text', label: item.label },
      ...innerMatches,
      { type: 'divider' },
    ];
  } else {
    return matchesSearch(item, search) ? [item] : [];
  }
}

function collapseDividers(model: readonly AnyMenuItem[]): AnyMenuItem[] {
  let out: AnyMenuItem[] = [];
  for (const add of model) {
    // No dividers at the start & no consecutive dividers
    if (
      add.type === 'divider' &&
      (out.length === 0 || out[out.length - 1].type === 'divider')
    ) {
      continue;
    }
    out.push(add);
  }
  // No dividers at the end
  while (out.length > 0 && out[out.length - 1].type === 'divider') {
    out.pop();
  }
  return out;
}

type MenuChildProps = {
  closeRootMenu: () => void;
  submenus: {
    item: SubMenuItemProps;
    close: () => void;
    toggle: () => void;
    isOpen: boolean;
    menuRef: React.RefObject<HTMLDivElement>;
  }[];
  isHighlighted: boolean;
  onMouseEnter: (e: React.MouseEvent) => void;
};

let __mousedownClock = 0;

/** Returns props to add to the inside element. */
export function useOnMousedownOutside(
  enabled: boolean,
  callback: () => void,
  ...outsideOf: React.RefObject<HTMLElement>[]
): { onPointerDown: (e: React.PointerEvent) => void } {
  const wasInside = React.useRef(new Set<number>());

  React.useEffect(() => {
    if (!enabled) return;
    const listener = (e: MouseEvent) => {
      if (wasInside.current.size) {
        wasInside.current.clear();
        return;
      }
      if (e.target instanceof Node) {
        for (const o of outsideOf) {
          if (o.current?.contains(e.target)) return;
        }
      }
      callback();
    };
    window.addEventListener('pointerdown', listener);
    return () => window.removeEventListener('pointerdown', listener);
  }, [enabled, callback, outsideOf]);

  return {
    onPointerDown: React.useCallback((e: React.PointerEvent) => {
      const now = __mousedownClock++;
      wasInside.current.add(now);
    }, []),
  };
}

let __mouseupClock = 0;

export function useOnMouseupOutside(
  enabled: boolean,
  callback: () => void,
  ...outsideOf: React.RefObject<HTMLElement>[]
): { onPointerUp: (e: React.PointerEvent) => void } {
  const wasInside = React.useRef(new Set<number>());

  React.useEffect(() => {
    if (!enabled) return;
    const listener = (e: MouseEvent) => {
      if (wasInside.current.size) {
        wasInside.current.clear();
        return;
      }
      if (e.target instanceof Node) {
        for (const o of outsideOf) {
          if (o.current?.contains(e.target)) return;
        }
      }
      callback();
    };
    window.addEventListener('pointerup', listener);
    return () => window.removeEventListener('pointerup', listener);
  }, [enabled, callback, outsideOf]);

  return {
    onPointerUp: React.useCallback((e: React.PointerEvent) => {
      const now = __mouseupClock++;
      wasInside.current.add(now);
    }, []),
  };
}

export type MenuDisplayOptions = {
  search?: 'hidden' | MenuSearchOptions;
  // So that ButtonProps & CommonMenuOptions don't overlap:
  menuVariant?: 'regular' | 'disjointed';
};
type MenuSearchOptions = {
  iconStart?: React.ComponentType | React.ReactNode;
  button?: {
    tooltip: string;
    onSearch: (value: string) => void;
  };
  placeholder?: string;
  hideWhenEmpty?: boolean;
};

// This is most used option set.  We make this a string "hidden" so that
// consumers don't accidentally create a new object every render.
const HIDDEN_SEARCH_OPTIONS: MenuSearchOptions = { hideWhenEmpty: true };

function twiddleDisjointedMenuLayout(menuEl: HTMLElement) {
  const left = parseFloat(menuEl.style.left || '0');
  // The search bar is 512px long, which is 256px longer than the menu's width.
  // So if the menu is on the right side of the screen, we need to move the search bar
  // to the left by up to 256px.
  const searchBarRight = left + 32 * 16;
  const pad = 4;
  const searchTranslate = -Math.max(
    0,
    searchBarRight - window.innerWidth + pad,
  );
  menuEl.style.setProperty(
    '--disjointedSearchTranslate',
    `${searchTranslate}px`,
  );
}

export function useRightClickMenu(
  contents: MenuModel,
  opts?: MenuDisplayOptions,
): [
  onContextMenu: (e: React.MouseEvent) => void,
  portal: React.ReactNode,
  isOpen: boolean,
] {
  const [openAt, setOpenAt] = React.useState<
    { x: number; y: number } | undefined
  >();
  const menuRef = React.useRef<HTMLDivElement>(null);

  const close = React.useCallback(() => setOpenAt(undefined), []);

  usePinPointPlacement(
    openAt,
    menuRef,
    React.useCallback((anchor, content, contentEl) => {
      placeMenuAroundAnchor(anchor, content, contentEl);
      twiddleDisjointedMenuLayout(contentEl);
    }, []),
  );
  const insideProps = useOnMousedownOutside(!!openAt, () =>
    setOpenAt(undefined),
  );

  const onContextMenu = React.useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setOpenAt({ x: e.clientX, y: e.clientY });
  }, []);

  if (contents.length === 0) {
    return [() => {}, null, false];
  }
  return [
    onContextMenu,
    openAt ? (
      <Layer onOutsideClick={close}>
        <ReactFocusLock persistentFocus={true}>
          <Menu
            {...opts}
            {...insideProps}
            model={contents}
            ref={menuRef}
            className={styles.menuFixed}
            isRoot={true}
            closeRootMenu={close}
            closeThisMenu={close}
          />
        </ReactFocusLock>
      </Layer>
    ) : null,
    !!openAt,
  ];
}

function MenuSearch({
  value,
  placeholder = 'Search actions...',
  iconStart,
  button,
  onChange,
  closeRootMenu,
  menuVariant,
  hideWhenEmpty,
}: {
  value: string;
  onChange: (value: string) => void;
  closeRootMenu: () => void;
  menuVariant: MenuDisplayOptions['menuVariant'];
} & MenuSearchOptions) {
  return (
    <div
      className={classNames(
        styles.search,
        value === '' && hideWhenEmpty && styles.isHidden,
      )}
    >
      <FormInput
        type="textarea"
        placeholder={placeholder}
        iconStart={iconStart}
        buttonEnd={
          button && (
            <Button
              size="small"
              variant="primary"
              background="ai"
              disabled={!value}
              icon={IconArrowForward}
              tooltip={button.tooltip}
              onClick={() => {
                button.onSearch(value);
                closeRootMenu();
              }}
            />
          )
        }
        value={value}
        onChange={onChange}
        autoFocus={!probablyVirtualKeyboard()}
        variant={menuVariant === 'disjointed' ? 'borderless' : 'regular'}
      />
    </div>
  );
}

function probablyVirtualKeyboard() {
  return (
    navigator.userAgent.includes('Android') ||
    navigator.userAgent.includes('iPhone') ||
    navigator.userAgent.includes('iPad')
  );
}

export const placeFixedWidthAroundAnchor: PlacementFunction = (
  anchor,
  content,
  contentEl,
) => {
  const pad = 4;
  const openRight = anchor.left + content.width >= window.innerWidth;
  const left = Math.max(
    pad,
    Math.min(
      openRight ? anchor.right - content.width : anchor.left,
      window.innerWidth - content.width - pad,
    ),
  );
  const openUpwards = anchor.bottom + content.height > window.innerHeight;
  const top = openUpwards
    ? Math.max(0, anchor.top - content.height - pad)
    : Math.min(anchor.bottom + pad, window.innerHeight - content.height);
  contentEl.style.top = top + 'px';
  contentEl.style.left = left + 'px';
};

export const placeMenuAroundAnchor: PlacementFunction = (
  anchor,
  content,
  contentEl,
) => {
  placeFixedWidthAroundAnchor(anchor, content, contentEl);
  contentEl.style.minWidth = anchor.width + 'px';
};

export function useAnchoredMenu(
  isOpen: boolean,
  close: () => void,
  anchorRef: React.RefObject<HTMLElement>,
  menu: MenuModel,
  opts?: MenuDisplayOptions & {
    closeOnAnchorMousedown?: boolean;
  },
): [portal: React.ReactNode] {
  const menuRef = React.useRef<HTMLDivElement>(null);

  const basePlaceFn =
    opts?.menuVariant === 'disjointed'
      ? placeFixedWidthAroundAnchor
      : placeMenuAroundAnchor;
  usePinElementPlacement(
    isOpen,
    anchorRef,
    menuRef,
    React.useCallback(
      (anchor, content, contentEl) => {
        basePlaceFn(anchor, content, contentEl);
        twiddleDisjointedMenuLayout(contentEl);
      },
      [basePlaceFn],
    ),
  );
  const insideProps = useOnMousedownOutside(
    isOpen,
    close,
    ...(opts?.closeOnAnchorMousedown ? [] : [anchorRef]),
  );

  const portal = (
    <CSSTransition
      in={isOpen}
      nodeRef={menuRef}
      timeout={100}
      classNames={styles}
      unmountOnExit
    >
      <Layer>
        <ReactFocusLock persistentFocus={true}>
          <Menu
            {...opts}
            {...insideProps}
            className={styles.menuFixed}
            ref={menuRef}
            model={menu}
            isRoot={true}
            closeRootMenu={close}
            closeThisMenu={close}
          />
        </ReactFocusLock>
      </Layer>
    </CSSTransition>
  );
  return [portal];
}

export function MenuButton({
  menu,
  onClose,
  onClick,
  onOpen,
  icon,
  ...passthrough
}: {
  menu: MenuModel;
  onClose?: () => void;
  onClick?: () => void;
  onOpen?: () => void;
} & MenuDisplayOptions &
  Omit<ComponentProps<typeof Button>, 'onClick' | 'checked'>) {
  const [isOpen, setIsOpenState] = React.useState(false);
  const buttonRef = React.useRef<HTMLDivElement>(null);

  const close = React.useCallback(() => {
    setIsOpenState(false);
    onClose?.();
  }, [onClose]);
  const onClickOrContextMenu = React.useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (isOpen) {
        close();
      } else {
        setIsOpenState(true);
        onOpen?.();
      }
      onClick?.();
    },
    [isOpen, onClick, close, onOpen],
  );

  const [portal] = useAnchoredMenu(isOpen, close, buttonRef, menu, passthrough);

  return (
    <>
      <Button
        ref={buttonRef}
        onClick={onClickOrContextMenu}
        onContextMenu={onClickOrContextMenu}
        checked={isOpen}
        icon={icon}
        {...(passthrough as ComponentProps<typeof Button>)}
      />
      {portal}
    </>
  );
}

export function DropdownIcon({
  isSubtle = true,
  size = 'medium',
}: {
  isSubtle?: boolean;
  size?: 'medium' | 'large';
}) {
  return (
    <div
      className={classNames(
        styles.dropdownIcon,
        isSubtle && styles.isSubtle,
        size === 'large' && styles.isLarge,
      )}
    >
      <IconChevronDown />
    </div>
  );
}

function MenuText({
  children,
  size = 'small',
}: {
  children: React.ReactNode;
  size?: 'tiny' | 'small';
}) {
  const TextComponent = size === 'tiny' ? TextTinyLight : TextSmallLight;
  return (
    <div className={styles.menuText}>
      <TextComponent color="grey">{children}</TextComponent>
    </div>
  );
}

function renderLabel(
  label: string,
  subLabel: string | undefined,
  labelCanWrap: boolean | undefined,
) {
  return (
    <Inline xalign="start" spacing={4}>
      <TextSmallLight lineClamp={labelCanWrap ? undefined : 1}>
        {label}
      </TextSmallLight>
      {subLabel && (
        <TextSmallLight color="grey" lineClamp={labelCanWrap ? undefined : 1}>
          {'• ' + subLabel}
        </TextSmallLight>
      )}
    </Inline>
  );
}

export const MenuItemView = React.forwardRef<
  HTMLElement,
  MenuItemProps & MenuChildProps
>(function _MenuItemView(props, propRef) {
  const {
    label,
    subLabel,
    labelCanWrap,
    description,
    icon,
    iconRight,
    componentRight,
    shortcut,
    checked,
    disabled,
    disabledReason,
    subtlyDisabled,
    hasSubmenuOpen,
    closeRootMenu,
    tooltip: tooltipProp,
    onClick,
    closeOnClick = true,
    onMouseEnter,
    onMouseLeave,
    onPointerDown,
    href,
    target,
    isHighlighted,
  } = props;

  const visiblyDisabled = disabled || subtlyDisabled;
  const tooltip = disabled && disabledReason ? disabledReason : tooltipProp;

  const myRef = React.useRef<HTMLElement>(null);
  const ref = useComposedRefs(propRef, myRef);

  const Tag = href ? ('a' as const) : ('button' as const);
  return (
    <Tag
      ref={ref as any}
      className={classNames(
        styles.menuItem,
        icon && styles.hasIcon,
        hasSubmenuOpen && styles.hasSubmenuOpen,
        isHighlighted && styles.isHighlighted,
        (description || labelCanWrap) && styles.dynamicHeight,
      )}
      href={href}
      target={target}
      onClick={(e) => {
        if (disabled) return;
        e.stopPropagation();
        closeOnClick && closeRootMenu();
        onClick?.();
      }}
      onMouseUp={(e) => {
        if (e.button === 2) {
          // holding the right click button in order to use the menu quickly
          e.preventDefault();
          if (disabled) return;
          closeOnClick && closeRootMenu();
          onClick?.();
        }
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onPointerDown={onPointerDown}
      aria-disabled={visiblyDisabled}
    >
      {tooltip && (
        <Tooltip label={tooltip} anchorRef={myRef} placement="horizontal" />
      )}
      {icon && <div className={styles.icon}>{renderIcon(icon)}</div>}
      <Rows spacing={0}>
        {renderLabel(label, subLabel, labelCanWrap)}
        {description && (
          <TextSmallLight color="grey">{description}</TextSmallLight>
        )}
      </Rows>
      {typeof componentRight === 'function'
        ? componentRight({ isHighlighted })
        : componentRight}
      {iconRight && <div className={styles.icon}>{renderIcon(iconRight)}</div>}
      {checked && (
        <div className={styles.check}>
          <IconCheck />
        </div>
      )}
      {shortcut && (
        <div className={styles.shortcut}>
          <TextTiny>{shortcut}</TextTiny>
        </div>
      )}
      {!visiblyDisabled && iconRight == null && !checked && (
        <div className={styles.focusedEnter}>
          <TextTiny>↵</TextTiny>
        </div>
      )}
    </Tag>
  );
});

function useComposedRefs<T>(
  ref1: React.Ref<T> | undefined,
  ref2: React.Ref<T> | undefined,
) {
  return React.useMemo(() => {
    if (!ref1 && !ref2) return undefined;
    return (el: T) => {
      if (ref1) {
        if (typeof ref1 === 'function') ref1(el);
        else (ref1 as React.MutableRefObject<T>).current = el;
      }
      if (ref2) {
        if (typeof ref2 === 'function') ref2(el);
        else (ref2 as React.MutableRefObject<T>).current = el;
      }
    };
  }, [ref1, ref2]);
}

const placeSubmenu: PlacementFunction = (anchor, content, contentEl) => {
  const pad = 4;
  const openRight = anchor.right + content.width <= window.innerWidth;
  let left = Math.max(
    pad,
    Math.min(
      openRight ? anchor.right : anchor.left - content.width,
      window.innerWidth - content.width - pad,
    ),
  );

  const openUpwards = anchor.bottom + content.height > window.innerHeight;
  let top = openUpwards ? anchor.bottom - content.height : anchor.top;

  /* We nest `position: fixed` and `position: absolute` elements, so we need to
   * translate the position we set in css.
   *
   * It was easier to nest things then fix that bug where mousedown inside of
   * a submenu counted as clicking outside the parent and closed the menu on
   * mousedown.
   */
  let at: HTMLElement = contentEl;
  while (true) {
    const parentEl = at.parentElement?.closest(
      `.${styles.menuSubmenu}, .${styles.menuFixed}`,
    );
    if (!(parentEl instanceof HTMLElement)) break;
    top -= parentEl.offsetTop;
    left -= parentEl.offsetLeft;
    at = parentEl;
  }

  contentEl.style.top = top + 'px';
  contentEl.style.left = left + 'px';
};

let mouseXWhenOpenedSubmenu = -1;

export function SubMenuItem({
  item,
  ...childProps
}: { item: SubMenuItemProps } & MenuChildProps) {
  const { menu, disabled, disabledReason } = item;
  const { submenus } = childProps;

  const [isOpen, setOpen] = React.useState(false);
  const openTimeoutId = React.useRef(-1);
  const closeTimeoutId = React.useRef(-1);
  const openNow = (e: React.MouseEvent) => {
    mouseXWhenOpenedSubmenu = e.clientX;
    window.clearTimeout(closeTimeoutId.current);
    window.clearTimeout(openTimeoutId.current);
    setOpen(true);
  };
  const handleMouseEnterMenuItem = (e: React.MouseEvent) => {
    childProps.onMouseEnter(e);
    if (isOpen) return;
    const siblingMenuOpen = submenus.some((other) => other.isOpen);
    const isMousingSideways =
      Math.abs(e.clientX - mouseXWhenOpenedSubmenu) > 10;
    if (siblingMenuOpen && isMousingSideways) {
      // The user is moving their mouse towards the menu opened by our sibling.
      openTimeoutId.current = window.setTimeout(() => openNow(e), 250);
    } else {
      openNow(e);
    }
  };
  const handleMouseLeave = () => {
    window.clearTimeout(openTimeoutId.current);
    window.clearTimeout(closeTimeoutId.current);
    closeTimeoutId.current = window.setTimeout(() => setOpen(false), 500);
  };

  const menuRef = React.useRef<HTMLDivElement>(null);
  const anchorRef = React.useRef<HTMLElement>(null);

  React.useEffect(() => {
    const submenuRef = {
      item,
      close: () => setOpen(false),
      toggle: () => setOpen((o) => !o),
      isOpen,
      menuRef,
    };
    submenus.push(submenuRef);
    return () => arrayDelete(submenus, submenuRef);
  }, [submenus, isOpen, item]);

  React.useEffect(() => {
    if (!isOpen) return;
    submenus.forEach((other) => {
      if (other.item === item) return;
      other.close();
    });
  }, [isOpen, item, submenus]);

  usePinElementPlacement(isOpen, anchorRef, menuRef, placeSubmenu);
  const insideProps = useOnMousedownOutside(isOpen, () => setOpen(false));

  if (disabled) {
    return (
      <MenuItemView
        {...item}
        disabled={true}
        disabledReason={disabledReason}
        {...childProps}
      />
    );
  }
  return (
    <>
      <MenuItemView
        {...item}
        {...insideProps}
        iconRight={<ColoredIcon icon={IconChevronRight} color="colorGrey" />}
        hasSubmenuOpen={isOpen}
        onClick={() => setOpen((v) => !v)}
        closeOnClick={false}
        onMouseLeave={handleMouseLeave}
        ref={anchorRef}
        {...childProps}
        onMouseEnter={handleMouseEnterMenuItem}
      />
      {isOpen && (
        <Menu
          {...insideProps}
          model={menu}
          className={styles.menuSubmenu}
          ref={menuRef}
          onMouseEnter={openNow}
          onMouseLeave={handleMouseLeave}
          isRoot={false}
          closeRootMenu={childProps.closeRootMenu}
          closeThisMenu={() => setOpen(false)}
        />
      )}
    </>
  );
}

export function MenuDivider() {
  return <div className={styles.divider} />;
}

function arrayDelete<T>(arr: T[], v: T) {
  const i = arr.indexOf(v);
  if (i === -1) return;
  arr.splice(i, 1);
}

function matchesSearch(
  item: Pick<MenuItemProps, 'label' | 'searchSynonyms'>,
  search: string,
) {
  if (!search) return true;
  if (search === '*') return true;
  const searchLower = search.toLowerCase();
  return (
    item.label.toLowerCase().includes(searchLower) ||
    item.searchSynonyms?.toLowerCase().includes(search)
  );
}
